import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItem,
  ListItemText,
  Switch,
  IconButton,
  List,
  Box,
  Typography,
  Divider,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAddRoleMutation, useRemoveRoleMutation } from 'services/users'
import { EUserRoles, IUser } from 'types/users'
import { Delete } from '@mui/icons-material'

interface Props {
  user: IUser
  handleClose: () => void
}

interface RolePermissions {
  COMMISSION_MEMBER: boolean
  COMMITTEE_MEMBER: boolean
  PROCUREMENT_SPECIALIST: boolean
  ISP_SPECIALIST: boolean
  ADMIN: boolean
  SUPER_ADMIN: boolean
  USER: boolean
}

export const UserRolesEdit: FunctionComponent<Props> = ({ user, handleClose }) => {
  const { t } = useTranslation()

  const [roles, setRoles] = useState<RolePermissions>({
    COMMISSION_MEMBER: user.userRoles.includes(EUserRoles.COMMISSION_MEMBER),
    COMMITTEE_MEMBER: user.userRoles.includes(EUserRoles.COMMITTEE_MEMBER),
    PROCUREMENT_SPECIALIST: user.userRoles.includes(
      EUserRoles.PROCUREMENT_SPECIALIST
    ),
    ISP_SPECIALIST: user.userRoles.includes(EUserRoles.ISP_SPECIALIST),
    ADMIN: user.userRoles.includes(EUserRoles.ADMIN),
    SUPER_ADMIN: user.userRoles.includes(EUserRoles.SUPER_ADMIN),
    USER: user.userRoles.includes(EUserRoles.USER),
  })

  const [addedRoles, setAddedRoles] = useState<string[]>([])
  const [removedRoles, setRemovedRoles] = useState<string[]>([])

  const [removeRole, { isLoading: isLoadingRemoveRole }] = useRemoveRoleMutation()
  const [addRole, { isLoading: isLoadingAddRole, isSuccess: isSuccessAddRole }] =
    useAddRoleMutation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoles({
      ...roles,
      [event.target.name]: event.target.checked,
    })
  }

  const handleSave = () => {
    if (removedRoles.length > 0) {
      removeRole({
        userId: user.id,
        roles: removedRoles,
      })
    }
    const userRoles = Object.keys(roles).filter(
      (key) => roles[key as keyof RolePermissions]
    )
    if (addedRoles.length > 0) {
      addRole({ userId: user.id, roles: userRoles })
    }
  }

  const handleRemoveRole = (role: string) => {
    setRoles((prevState) => ({
      ...prevState,
      [role as keyof RolePermissions]: false,
    }))

    setRemovedRoles((prevState) => [...prevState, role])
  }

  const handleAddRole = (role: string) => {
    setRoles((prevState) => ({
      ...prevState,
      [role as keyof RolePermissions]: false,
    }))

    setAddedRoles((prevState) => [...prevState, role])
  }

  useEffect(() => {
    if (isSuccessAddRole) {
      toast.success(t('operation-success'))
      handleClose()
    }
  }, [isSuccessAddRole, handleClose, t])

  return (
    <BootstrapDialog open={true} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {`${user.firstName} ${user.lastName}`}
      </DialogTitle>

      <DialogCloseButton handleClose={handleClose} />

      <DialogContent dividers>
        <List>
          {Object.keys(roles)
            .filter((key) => roles[key as keyof RolePermissions])
            .map((item) => {
              return (
                <ListItem
                  key={item}
                  secondaryAction={
                    <IconButton
                      onClick={() => handleRemoveRole(item)}
                      edge="end"
                      aria-label="delete"
                    >
                      <Delete color="error" />
                    </IconButton>
                  }
                >
                  <ListItemText>
                    <Box display="`flex" alignItems="center" gap={5}>
                      <Typography>
                        {t(EUserRoles[item as keyof RolePermissions].toLowerCase())}
                      </Typography>
                    </Box>
                  </ListItemText>
                  <Divider component={'li'} />
                </ListItem>
              )
            })}
        </List>

        {Object.keys(roles)
          .filter((key) => !roles[key as keyof RolePermissions])
          .map((item) => {
            return (
              <FormControlLabel
                key={item}
                label={t(EUserRoles[item as keyof RolePermissions].toLowerCase())}
                control={
                  <Switch
                    checked={roles[item as keyof RolePermissions]}
                    onChange={handleChange}
                    name={EUserRoles[item as keyof RolePermissions]}
                    onClick={() => handleAddRole(item)}
                  />
                }
                sx={{ display: 'block' }}
              />
            )
          })}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isLoadingAddRole || isLoadingRemoveRole}
          onClick={handleSave}
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          autoFocus
          sx={{ mr: 1 }}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  )
}
